import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

// ─── Paleta exacta del lab2 ───────────────────────────────────────────────────
const C = {
  bgPrimary:    '#0b0e17',
  bgCard:       '#161d2e',
  bgCardHover:  '#1c2540',
  borderSubtle: '#2a3250',
  borderAccent: '#4f46e5',
  textPrimary:  '#f1f5f9',
  textSecondary:'#94a3b8',
  textMuted:    '#64748b',
  accentPurple: '#7c3aed',
  accentIndigo: '#4f46e5',
  accentBlue:   '#3b82f6',
  success:      '#10b981',
  error:        '#ef4444',
  warning:      '#f59e0b',
  stripBg:      '#1e2740',
  stripBorder:  '#334155',
  inputBg:      '#0f172a',
};

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface TetonorSolutionCell { valor: number; op: string }
interface TetonorPositionPair { pairIndex: number; role: 'a' | 'b' }

interface TetonorPuzzleData {
  id: number;
  grid: number[][];
  strip: (number | null)[];
  solution: {
    strip: number[];
    pairs: { a: number; b: number; suma: number; producto: number }[];
    cells: TetonorSolutionCell[][];
    positionToPair: TetonorPositionPair[];
  };
  meta: { min: number; max: number; ocultos: number; intentos: number };
  nivelNeureka: number;
  nivelNeurekaNombre: string;
  nivelNeurekaEmoji: string;
}

interface CellState { a: string; op: '+' | '×'; b: string }
type StripState = (string | null)[]; // null = visible fijo, string = editable

// ─── Helpers ──────────────────────────────────────────────────────────────────

function arithmeticStatus(cell: CellState, target: number): 'ok' | 'bad' | 'empty' {
  const a = parseInt(cell.a), b = parseInt(cell.b);
  if (isNaN(a) || isNaN(b)) return 'empty';
  return (cell.op === '+' ? a + b : a * b) === target ? 'ok' : 'bad';
}

// ─── Análisis de la tira ──────────────────────────────────────────────────────
// Marca cada posición de la tira a partir del trabajo REAL del jugador (no del
// reparto interno de la solución única), para que:
//   · las posiciones seguras (visibles / ya escritas) se tachen antes que los
//     huecos misteriosos,
//   · se avise cuando un operando se usa más veces de las que existe en la tira,
//   · un hueco vacío se deduzca y complete cuando una pareja ya resuelta lo
//     obliga.
// Solo cuentan celdas con aritmética correcta cuyo (operandos, operación,
// objetivo) coincide con una pareja real de la solución: así el marcado no se
// enciende por combinaciones aritméticamente válidas pero ajenas a la solución.

interface StripPosBadge { sum: boolean; prod: boolean; struck: boolean; overuse: boolean }

interface StripAnalysis {
  posBadge: StripPosBadge[];
  deductions: { pos: number; value: number }[];
  overuse: boolean;
}

function analyzeStrip(
  cells: CellState[],
  stripInputs: StripState,
  puzzle: TetonorPuzzleData,
): StripAnalysis {
  const empty: StripAnalysis = {
    posBadge: Array.from({ length: 16 }, () => ({ sum: false, prod: false, struck: false, overuse: false })),
    deductions: [],
    overuse: false,
  };
  if (cells.length < 16 || stripInputs.length < 16) return empty;

  const gridFlat = puzzle.grid.flat();
  const solStrip = puzzle.solution.strip; // tira completa correcta, ordenada

  // cap[v] = veces que v aparece en la tira solución
  const cap = new Map<number, number>();
  for (const v of solStrip) cap.set(v, (cap.get(v) ?? 0) + 1);

  // Claves de parejas reales de la solución, por rol (+ / ×) y objetivo
  const solKeys = new Set<string>();
  for (const p of puzzle.solution.pairs) {
    const lo = Math.min(p.a, p.b), hi = Math.max(p.a, p.b);
    solKeys.add(`${lo},${hi}|+|${p.suma}`);
    solKeys.add(`${lo},${hi}|x|${p.producto}`);
  }

  // Valor conocido de cada posición: fijo visible o escrito por el jugador
  const known: (number | null)[] = puzzle.strip.map((fixed, i) => {
    if (fixed !== null) return fixed;
    const t = stripInputs[i];
    return t !== null && t !== '' ? parseInt(t as string) : null;
  });
  const knownCount = new Map<number, number>();
  for (const v of known) if (v !== null) knownCount.set(v, (knownCount.get(v) ?? 0) + 1);

  // Uso de cada operando en celdas correctas que reproducen una pareja real
  const sumUse = new Map<number, number>();
  const prodUse = new Map<number, number>();
  for (let i = 0; i < 16; i++) {
    const c = cells[i];
    if (arithmeticStatus(c, gridFlat[i]) !== 'ok') continue;
    const a = parseInt(c.a), b = parseInt(c.b);
    const lo = Math.min(a, b), hi = Math.max(a, b);
    const key = `${lo},${hi}|${c.op === '+' ? '+' : 'x'}|${gridFlat[i]}`;
    if (!solKeys.has(key)) continue;
    const target = c.op === '+' ? sumUse : prodUse;
    target.set(a, (target.get(a) ?? 0) + 1);
    target.set(b, (target.get(b) ?? 0) + 1);
  }

  // Deducción: huecos vacíos que una pareja ya resuelta (suma Y producto)
  // obliga a rellenar. El valor sale de la solución (siempre correcto) y solo
  // se rellena si el propio trabajo del jugador necesita más de ese número que
  // los ya visibles/escritos.
  const deductions: { pos: number; value: number }[] = [];
  const filledForValue = new Map<number, number>();
  for (let i = 0; i < 16; i++) {
    if (puzzle.strip[i] !== null || known[i] !== null) continue; // no es hueco vacío
    const v = solStrip[i];
    const need = Math.min(sumUse.get(v) ?? 0, prodUse.get(v) ?? 0);
    const have = (knownCount.get(v) ?? 0) + (filledForValue.get(v) ?? 0);
    if (need > have) {
      deductions.push({ pos: i, value: v });
      filledForValue.set(v, (filledForValue.get(v) ?? 0) + 1);
    }
  }

  // Valor efectivo de cada posición (conocido + deducido) para repartir marcas
  const effValue = known.slice();
  for (const d of deductions) effValue[d.pos] = d.value;

  const byValue = new Map<number, number[]>();
  effValue.forEach((v, i) => {
    if (v === null) return;
    if (!byValue.has(v)) byValue.set(v, []);
    byValue.get(v)!.push(i);
  });

  const posBadge: StripPosBadge[] = Array.from({ length: 16 }, () => ({ sum: false, prod: false, struck: false, overuse: false }));
  let overuse = false;

  for (const [v, positions] of byValue) {
    // Prioridad: posiciones visibles fijas antes que escritas / deducidas
    positions.sort((p, q) => {
      const pk = puzzle.strip[p] !== null ? 0 : 1;
      const qk = puzzle.strip[q] !== null ? 0 : 1;
      return pk - qk || p - q;
    });
    const capV = cap.get(v) ?? positions.length;
    const s = sumUse.get(v) ?? 0;
    const pr = prodUse.get(v) ?? 0;
    const over = s > capV || pr > capV;
    if (over) overuse = true;
    positions.forEach((pos, k) => {
      const b = posBadge[pos];
      if (k < Math.min(s, capV)) b.sum = true;
      if (k < Math.min(pr, capV)) b.prod = true;
      b.struck = b.sum && b.prod;
      b.overuse = over;
    });
  }

  return { posBadge, deductions, overuse };
}

const DIFFICULTIES = [
  { orden: 1, nombre: 'Neurona',  icono: '🧠' },
  { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
  { orden: 3, nombre: 'Cerebro',  icono: '🧩' },
  { orden: 4, nombre: 'Genio',    icono: '💡' },
  { orden: 5, nombre: 'Einstein', icono: '🎓' },
];

// ─── Componente ───────────────────────────────────────────────────────────────

export default function TetonorGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();

  const [puzzle, setPuzzle]           = useState<TetonorPuzzleData | null>(null);
  const [cells, setCells]             = useState<CellState[]>([]);
  const [stripInputs, setStripInputs] = useState<StripState>([]);
  const [history, setHistory]         = useState<{ cells: CellState[]; strip: StripState }[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [startTime]                   = useState(Date.now());
  const [moves, setMoves]             = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [forceReload, setForceReload] = useState(0);
  const [cellErrors, setCellErrors]   = useState<boolean[]>(Array(16).fill(false));
  const [toast, setToast]             = useState('');
  const [showCalc, setShowCalc]       = useState(false);
  const [calcDisplay, setCalcDisplay] = useState('0');
  const [calcFirst, setCalcFirst]     = useState<number | null>(null);
  const [calcOp, setCalcOp]           = useState<string | null>(null);
  const [calcWaiting, setCalcWaiting] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const dificultadOrden = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('tetonor', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ─── Toast ─────────────────────────────────────────────────────────────────

  const showToast = (msg: string, _type: 'error' | 'success' = 'error') => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // ─── Carga ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.get('/tetonor/board', { params: { dificultad: dificultadOrden } });
        const data: TetonorPuzzleData = res.data.data;
        setPuzzle(data);
        initState(data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al cargar el puzzle');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dificultadOrden, forceReload]);

  function initState(data: TetonorPuzzleData) {
    const initCells: CellState[]  = Array(16).fill(null).map(() => ({ a: '', op: '+', b: '' }));
    const initStrip: StripState   = data.strip.map(v => v === null ? '' : null);
    setCells(initCells);
    setStripInputs(initStrip);
    setHistory([{ cells: initCells, strip: initStrip }]);
    setHistoryIndex(0);
    setMoves(0);
    setIsCompleted(false);
    setShowVictory(false);
    setCellErrors(Array(16).fill(false));
    setError('');
  }

  // ─── Historial ─────────────────────────────────────────────────────────────

  const pushHistory = (newCells: CellState[], newStrip: StripState) => {
    setHistory(prev => {
      const sliced = prev.slice(0, historyIndex + 1);
      const next = [...sliced, { cells: newCells, strip: newStrip }];
      return next.length > 50 ? next.slice(1) : next;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  };

  const handleUndo = () => {
    if (historyIndex <= 0) return;
    const prev = history[historyIndex - 1];
    setCells(prev.cells); setStripInputs(prev.strip);
    setHistoryIndex(historyIndex - 1);
  };

  const handleRedo = () => {
    if (historyIndex >= history.length - 1) return;
    const next = history[historyIndex + 1];
    setCells(next.cells); setStripInputs(next.strip);
    setHistoryIndex(historyIndex + 1);
  };

  // ─── Edición ───────────────────────────────────────────────────────────────

  const updateCell = (idx: number, field: keyof CellState, value: string) => {
    if (isCompleted || !puzzle) return;
    const next = cells.map((c, i) => i === idx ? { ...c, [field]: value } : c);
    setCells(next); setMoves(m => m + 1);
    pushHistory(next, stripInputs);
    checkAutoVictory(next, stripInputs);
  };

  const toggleOp = (idx: number) => {
    if (isCompleted || !puzzle) return;
    const next = cells.map((c, i) => i === idx ? { ...c, op: c.op === '+' ? '×' as const : '+' as const } : c);
    setCells(next); setMoves(m => m + 1);
    pushHistory(next, stripInputs);
    checkAutoVictory(next, stripInputs);
  };

  const updateStrip = (pos: number, value: string) => {
    if (isCompleted || !puzzle) return;
    const next = stripInputs.map((v, i) => i === pos ? value : v);
    setStripInputs(next); setMoves(m => m + 1);
    pushHistory(cells, next);
    checkAutoVictory(cells, next);
  };

  // ─── Pista ─────────────────────────────────────────────────────────────────

  const handleHint = () => {
    if (!puzzle || isCompleted) return;
    const gridFlat = puzzle.grid.flat();
    const flatSol  = puzzle.solution.cells.flat();
    const firstEmpty = cells.findIndex((c, i) => arithmeticStatus(c, gridFlat[i]) !== 'ok');
    if (firstEmpty === -1) return;
    const sol = flatSol[firstEmpty];
    const isPlus = sol.op.includes('+');
    const [aPart, bPart] = isPlus ? sol.op.split('+') : sol.op.split('×');
    const next = cells.map((c, i) =>
      i === firstEmpty ? { a: aPart.trim(), op: isPlus ? '+' as const : '×' as const, b: bPart.trim() } : c
    );
    setCells(next); setMoves(m => m + 1);
    pushHistory(next, stripInputs);
    checkAutoVictory(next, stripInputs);
  };

  // ─── Solución ──────────────────────────────────────────────────────────────

  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Ver la solución? No se guardará el progreso.')) return;
    const flatSol = puzzle.solution.cells.flat();
    const nextCells: CellState[] = flatSol.map(s => {
      const isPlus = s.op.includes('+');
      const [aPart, bPart] = isPlus ? s.op.split('+') : s.op.split('×');
      return { a: aPart.trim(), op: isPlus ? '+' : '×', b: bPart.trim() };
    });
    const nextStrip: StripState = puzzle.strip.map((v, i) =>
      v === null ? String(puzzle.solution.strip[i]) : null
    );
    setCells(nextCells); setStripInputs(nextStrip);
    pushHistory(nextCells, nextStrip);
  };

  // ─── Reiniciar ─────────────────────────────────────────────────────────────

  const handleReset = () => {
    if (!puzzle || !window.confirm('¿Reiniciar el tablero?')) return;
    initState(puzzle);
  };

  // ─── Validar (manual) ──────────────────────────────────────────────────────

  const handleValidate = async () => {
    if (!puzzle || isCompleted) return;
    const gridFlat = puzzle.grid.flat();

    const newErrors = cells.map((c, i) => arithmeticStatus(c, gridFlat[i]) === 'bad');
    const anyBad = newErrors.some(Boolean);
    const anyEmpty = cells.some((c, i) => arithmeticStatus(c, gridFlat[i]) === 'empty');

    if (anyEmpty) { showToast('Aún hay celdas sin rellenar'); return; }
    if (anyBad)   { setCellErrors(newErrors); showToast('Algunas celdas tienen aritmética incorrecta'); return; }

    const missingStrip = stripInputs.some((v, i) => puzzle.strip[i] === null && (v === '' || v === null));
    if (missingStrip) { showToast('Faltan valores ocultos en la tira'); return; }

    await runValidation(cells, stripInputs, true);
  };

  // ─── Comprobación automática y validación ──────────────────────────────────

  const checkAutoVictory = useCallback(async (
    currentCells: CellState[],
    currentStrip: StripState,
  ) => {
    if (!puzzle || isCompleted) return;
    const gridFlat = puzzle.grid.flat();
    const allOk = currentCells.every((c, i) => arithmeticStatus(c, gridFlat[i]) === 'ok');
    if (!allOk) return;
    const anyEmpty = currentStrip.some((v, i) => puzzle.strip[i] === null && (v === '' || v === null));
    if (anyEmpty) return;
    await runValidation(currentCells, currentStrip, false);
  }, [puzzle, isCompleted]);

  const runValidation = async (
    currentCells: CellState[],
    _currentStrip: StripState,
    manual: boolean,
  ) => {
    if (!puzzle) return;
    const cellsPayload = currentCells.map(c => ({ a: parseInt(c.a), op: c.op, b: parseInt(c.b) }));
    try {
      const res = await api.post('/tetonor/validate', {
        solution: puzzle.solution,
        cells: [
          cellsPayload.slice(0, 4),
          cellsPayload.slice(4, 8),
          cellsPayload.slice(8, 12),
          cellsPayload.slice(12, 16),
        ],
      });
      if (res.data.data.correct) {
        setIsCompleted(true);
        setShowVictory(true);
        setCellErrors(Array(16).fill(false));
        const tiempo = Math.floor((Date.now() - startTime) / 1000);
        await saveProgress(tiempo, moves);
      } else {
        showToast('La aritmética es correcta, pero revisa los operandos — algún par no cuadra con la tira');
      }
    } catch {
      if (manual) showToast('Error al validar la solución');
    }
  };

  const saveProgress = async (tiempo: number, movimientos: number) => {
    if (!puzzle) return;
    try {
      await saveGameProgress({ tablero_id: puzzle.id, tiempo, movimientos, completado: true });
      await saveSessionResult({ tiempo, movimientos, completado: true, datos_juego: { tablero_id: puzzle.id } });
    } catch { /* no crítico */ }
  };

  // ─── Calculadora ───────────────────────────────────────────────────────────

  const calcCompute = (a: number, b: number, op: string) => {
    if (op === '+') return a + b;
    if (op === '−') return a - b;
    if (op === '×') return a * b;
    if (op === '÷') return b !== 0 ? Math.round((a / b) * 1e6) / 1e6 : 0;
    return b;
  };

  const calcInput = (digit: string) => {
    if (calcWaiting) { setCalcDisplay(digit); setCalcWaiting(false); }
    else setCalcDisplay(calcDisplay === '0' ? digit : calcDisplay.length < 12 ? calcDisplay + digit : calcDisplay);
  };

  const calcOperator = (op: string) => {
    const current = parseFloat(calcDisplay);
    if (calcFirst !== null && !calcWaiting) {
      const result = calcCompute(calcFirst, current, calcOp!);
      setCalcDisplay(String(result)); setCalcFirst(result);
    } else { setCalcFirst(current); }
    setCalcOp(op); setCalcWaiting(true);
  };

  const calcEquals = () => {
    if (calcFirst === null || calcOp === null) return;
    const result = calcCompute(calcFirst, parseFloat(calcDisplay), calcOp);
    setCalcDisplay(String(result));
    setCalcFirst(null); setCalcOp(null); setCalcWaiting(false);
  };

  const calcClear = () => { setCalcDisplay('0'); setCalcFirst(null); setCalcOp(null); setCalcWaiting(false); };
  const calcBackspace = () => { setCalcDisplay(calcDisplay.length <= 1 ? '0' : calcDisplay.slice(0, -1)); };
  const calcDot = () => { if (!calcDisplay.includes('.')) setCalcDisplay(calcDisplay + '.'); };

  // ─── Uso en tira ───────────────────────────────────────────────────────────

  const stripAnalysis = useMemo(
    () => (puzzle ? analyzeStrip(cells, stripInputs, puzzle) : null),
    [cells, stripInputs, puzzle],
  );

  // Rellena automáticamente los huecos que una pareja ya resuelta obliga.
  useEffect(() => {
    if (!puzzle || isCompleted || !stripAnalysis || stripAnalysis.deductions.length === 0) return;
    const next = stripInputs.map((v, i) => {
      const d = stripAnalysis.deductions.find(dd => dd.pos === i);
      return d ? String(d.value) : v;
    });
    setStripInputs(next);
    pushHistory(cells, next);
    showToast(
      `Deducido de tus parejas: se completó ${stripAnalysis.deductions.length === 1 ? 'un hueco' : `${stripAnalysis.deductions.length} huecos`} de la tira`,
      'success',
    );
    checkAutoVictory(cells, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripAnalysis]);

  // Aviso al sobrepasar el cupo de un operando (flanco de subida).
  const [overuseNotified, setOveruseNotified] = useState(false);
  useEffect(() => {
    const over = stripAnalysis?.overuse ?? false;
    if (over && !overuseNotified) {
      showToast('Has usado un número de la tira más veces de las que existe — revisa esas parejas');
      setOveruseNotified(true);
    } else if (!over && overuseNotified) {
      setOveruseNotified(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripAnalysis]);

  // ─── Estilos de celda ──────────────────────────────────────────────────────

  function cellBorderStyle(i: number): React.CSSProperties {
    if (cellErrors[i]) return { border: `2.5px solid ${C.error}`, background: 'rgba(239,68,68,0.12)', boxShadow: `0 0 20px rgba(239,68,68,0.35)` };
    const status = puzzle ? arithmeticStatus(cells[i], puzzle.grid.flat()[i]) : 'empty';
    if (status === 'ok')  return { border: `1.5px dashed #2dd4bf`, boxShadow: '0 0 8px rgba(45,212,191,0.15)' };
    if (status === 'bad') return { border: `1.5px solid #fb923c`, boxShadow: '0 0 8px rgba(251,146,60,0.15)' };
    return { border: `1.5px solid ${C.borderSubtle}` };
  }

  // ─── Loading ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: C.bgPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1.5rem' }}>
        <div style={{ width: 48, height: 48, border: `3px solid ${C.borderSubtle}`, borderTopColor: C.accentPurple, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <span style={{ color: C.textSecondary, fontWeight: 500 }}>Generando puzzle...</span>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div style={{ minHeight: '100vh', background: C.bgPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: C.error, fontSize: '1.1rem' }}>❌ {error || 'Error al cargar puzzle'}</span>
      </div>
    );
  }

  const gridFlat = puzzle.grid.flat();

  return (
    <div style={{ minHeight: '100vh', background: C.bgPrimary, display: 'flex', flexDirection: 'column', fontFamily: 'Inter, -apple-system, sans-serif', color: C.textPrimary, position: 'relative' }}>

      {isInSession && sessionType && (
        <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />
      )}

      {/* ── Barra superior (estilo Neureka) ── */}
      <div style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderBottom: `1px solid ${C.borderSubtle}` }}>

        <span style={{ fontWeight: 800, fontSize: '0.95rem', color: C.textPrimary, whiteSpace: 'nowrap' }}>🧮 Tetonor</span>
        <div style={{ width: 1, height: 20, background: C.borderSubtle, margin: '0 4px' }} />

        {/* Dificultades */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {DIFFICULTIES.map(d => (
            <button key={d.orden}
              onClick={() => dificultadOrden === d.orden ? setForceReload(p => p + 1) : navigate(`/juegos/tetonor/${d.orden}`)}
              style={{
                padding: '4px 10px', borderRadius: 8, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', transition: 'all .2s',
                background: dificultadOrden === d.orden ? C.textPrimary : 'rgba(255,255,255,0.1)',
                color: dificultadOrden === d.orden ? C.bgPrimary : C.textSecondary,
                border: dificultadOrden === d.orden ? 'none' : `1px solid ${C.borderSubtle}`,
              }}>
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        {/* Acciones */}
        {[
          { title: 'Deshacer', disabled: historyIndex <= 0, onClick: handleUndo, svg: <path d="M3 7v6h6"/>, path2: <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/> },
        ].map(() => null)}

        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {/* Deshacer */}
          <TopBtn title="Deshacer" disabled={historyIndex <= 0} onClick={handleUndo}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </TopBtn>
          {/* Rehacer */}
          <TopBtn title="Rehacer" disabled={historyIndex >= history.length - 1} onClick={handleRedo}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </TopBtn>
          {/* Pista */}
          <TopBtn title="Pista" disabled={isCompleted} onClick={handleHint}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </TopBtn>
          {/* Ver solución */}
          <TopBtn title="Ver solución" disabled={isCompleted} onClick={handleShowSolution}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
          </TopBtn>
          {/* Reiniciar */}
          <TopBtn title="Reiniciar" onClick={handleReset}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </TopBtn>
          {/* Nuevo */}
          <TopBtn title="Nuevo tablero" onClick={() => setForceReload(p => p + 1)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </TopBtn>
          {/* Cómo se juega */}
          <TopBtn title="Cómo se juega" onClick={() => setShowHowToPlay(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01"/></svg>
          </TopBtn>
          {/* Calculadora */}
          <TopBtn title={showCalc ? 'Cerrar calculadora' : 'Calculadora'} onClick={() => setShowCalc(c => !c)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="14" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="10" y2="18"/><line x1="14" y1="18" x2="16" y2="18"/></svg>
          </TopBtn>
        </div>
      </div>

      {/* ── Contenido principal ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem 1rem', gap: '1.25rem', overflowY: 'auto' }}>

        {/* Cuadrícula 4×4 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.6rem', width: '100%', maxWidth: 560 }}>
          {gridFlat.map((target, i) => (
            <div key={i} style={{
              background: C.bgCard, borderRadius: 12, padding: '0.4rem',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem',
              transition: 'all .25s',
              ...cellBorderStyle(i),
            }}>
              {/* Número objetivo */}
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: cellErrors[i] ? '#fca5a5' : C.textPrimary, lineHeight: 1, padding: '0.4rem 0 0.2rem' }}>
                {target}
              </span>

              {/* Inputs: a | op | b */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 2, width: '100%' }}>
                <input
                  type="text" inputMode="numeric" maxLength={3} value={cells[i].a}
                  placeholder="a"
                  onChange={e => updateCell(i, 'a', e.target.value.replace(/[^0-9]/g, ''))}
                  style={{ background: C.inputBg, border: `1px solid ${C.borderSubtle}`, color: cells[i].a ? C.accentPurple : C.textMuted, fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 600, textAlign: 'center', padding: '0.25rem 0.1rem', borderRadius: 6, outline: 'none', width: '100%', minWidth: 0 }}
                />
                <button onClick={() => toggleOp(i)} style={{
                  background: C.inputBg, border: `1px solid ${C.borderSubtle}`, color: C.accentBlue,
                  fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 700,
                  padding: '0.25rem 0.4rem', borderRadius: 6, cursor: 'pointer',
                  userSelect: 'none', transition: 'background .15s', flexShrink: 0,
                }}>
                  {cells[i].op}
                </button>
                <input
                  type="text" inputMode="numeric" maxLength={3} value={cells[i].b}
                  placeholder="b"
                  onChange={e => updateCell(i, 'b', e.target.value.replace(/[^0-9]/g, ''))}
                  style={{ background: C.inputBg, border: `1px solid ${C.borderSubtle}`, color: cells[i].b ? C.accentPurple : C.textMuted, fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 600, textAlign: 'center', padding: '0.25rem 0.1rem', borderRadius: 6, outline: 'none', width: '100%', minWidth: 0 }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Tira de operandos — 2 filas de 8 */}
        <div style={{ width: '100%', maxWidth: 560 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', textAlign: 'center' }}>
            Tira — números ordenados
          </div>
          {[puzzle.strip.slice(0, 8), puzzle.strip.slice(8, 16)].map((row, rowIdx) => (
            <div key={rowIdx} style={{ display: 'flex', justifyContent: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
              {row.map((fixedVal, colIdx) => {
                const pos = rowIdx * 8 + colIdx;
                const isHidden = fixedVal === null;
                const usage = stripAnalysis?.posBadge[pos] ?? { sum: false, prod: false, struck: false, overuse: false };
                const bothUsed = usage.struck;
                const anyUsed = usage.sum || usage.prod;

                const stripStyle: React.CSSProperties = {
                  width: 44, height: 44, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.85rem', fontWeight: 600,
                  flexShrink: 0, position: 'relative', transition: 'all .2s',
                  ...(isHidden
                    ? { background: 'rgba(124,58,237,0.05)', border: `1.5px dashed ${C.accentPurple}`, color: C.accentPurple }
                    : { background: C.stripBg, border: `1.5px solid ${C.stripBorder}`, color: C.textPrimary }
                  ),
                  ...(bothUsed ? { opacity: 0.5, textDecoration: 'line-through', borderColor: C.success, background: 'rgba(16,185,129,0.1)' } : {}),
                  ...(!bothUsed && usage.sum  ? { borderColor: C.accentBlue, background: 'rgba(59,130,246,0.08)' } : {}),
                  ...(!bothUsed && usage.prod ? { borderColor: C.warning,    background: 'rgba(245,158,11,0.08)' } : {}),
                  ...(usage.overuse ? { borderColor: C.error, background: 'rgba(239,68,68,0.15)', boxShadow: '0 0 12px rgba(239,68,68,0.4)', opacity: 1, textDecoration: 'none' } : {}),
                };

                const badges = (
                  <>
                    {usage.sum && (
                      <span style={{
                        position: 'absolute', top: -5, left: -5, width: 16, height: 16,
                        borderRadius: '50%', fontSize: '0.6rem', fontWeight: 800, color: 'white',
                        background: C.accentBlue, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(59,130,246,.4)', pointerEvents: 'none',
                      }}>+</span>
                    )}
                    {usage.prod && (
                      <span style={{
                        position: 'absolute', top: -5, right: -5, width: 16, height: 16,
                        borderRadius: '50%', fontSize: '0.6rem', fontWeight: 800, color: 'white',
                        background: C.warning, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(245,158,11,.4)', pointerEvents: 'none',
                      }}>×</span>
                    )}
                  </>
                );

                if (!isHidden) {
                  return (
                    <div key={pos} style={stripStyle}>
                      {fixedVal}
                      {anyUsed && badges}
                    </div>
                  );
                }

                // Slot oculto con input
                return (
                  <div key={pos} style={{ ...stripStyle, ...(stripInputs[pos] ? { borderStyle: 'solid', background: 'rgba(124,58,237,0.1)' } : {}) }}>
                    <input
                      type="text" inputMode="numeric" maxLength={3}
                      value={stripInputs[pos] as string ?? ''}
                      placeholder="?"
                      onChange={e => updateStrip(pos, e.target.value.replace(/[^0-9]/g, ''))}
                      style={{
                        background: 'transparent', border: 'none', outline: 'none',
                        color: C.accentPurple, fontFamily: 'inherit',
                        fontSize: '0.85rem', fontWeight: 700, textAlign: 'center',
                        width: '100%', height: '100%', borderRadius: '50%',
                      }}
                    />
                    {anyUsed && badges}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Botones de acción */}
        <div style={{ display: 'flex', gap: '0.75rem', width: '100%', maxWidth: 560, paddingBottom: '0.5rem' }}>
          <button onClick={handleValidate} disabled={isCompleted} style={{
            flex: 1, background: `linear-gradient(135deg, ${C.accentIndigo}, ${C.accentPurple})`,
            color: 'white', border: 'none', borderRadius: 12, padding: '0.85rem 1.5rem',
            fontFamily: 'inherit', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer',
            opacity: isCompleted ? 0.5 : 1, transition: 'all .3s',
          }}>
            ✓ Validar
          </button>
          <button onClick={() => setForceReload(p => p + 1)} style={{
            background: C.bgCard, color: C.textSecondary, border: `1px solid ${C.borderSubtle}`,
            borderRadius: 12, padding: '0.85rem 1.25rem',
            fontFamily: 'inherit', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
            transition: 'all .2s',
          }}>
            Nuevo
          </button>
        </div>
      </div>

      {/* ── Calculadora (sidebar desktop) ── */}
      {showCalc && windowWidth > 960 && (
        <div style={{ width: 280, borderLeft: `1px solid ${C.borderSubtle}`, display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' }}>
          <CalcContent
            display={calcDisplay} first={calcFirst} op={calcOp} waiting={calcWaiting}
            onDigit={calcInput} onDot={calcDot} onOp={calcOperator}
            onEquals={calcEquals} onClear={calcClear} onBackspace={calcBackspace}
          />
        </div>
      )}
      </div>

      {/* ── Calculadora (flotante mobile/tablet) ── */}
      {showCalc && windowWidth <= 960 && (
        <div style={{ position: 'fixed', bottom: '5rem', right: '1rem', width: 272, borderRadius: 16, overflow: 'hidden', zIndex: 200, boxShadow: '0 20px 60px rgba(0,0,0,0.7)', border: `1px solid ${C.borderSubtle}` }}>
          <CalcContent
            display={calcDisplay} first={calcFirst} op={calcOp} waiting={calcWaiting}
            onDigit={calcInput} onDot={calcDot} onOp={calcOperator}
            onEquals={calcEquals} onClear={calcClear} onBackspace={calcBackspace}
          />
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '2rem', left: '50%', transform: 'translateX(-50%)',
          background: C.bgCard, border: `1px solid ${C.borderAccent}`, borderRadius: 12,
          padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: 600, color: C.textPrimary,
          zIndex: 300, boxShadow: '0 10px 40px rgba(0,0,0,.4)', pointerEvents: 'none',
        }}>
          {toast}
        </div>
      )}

      {/* ── Modales ── */}
      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Tetonor"
        gameIcon="🧮"
        objective="Descubre qué pareja de números genera cada casilla mediante suma y producto."
        rules={[
          'La cuadrícula 4×4 tiene 16 casillas, cada una con un número objetivo.',
          'La tira inferior tiene 16 operandos ordenados de menor a mayor (algunos ocultos).',
          'Esos 16 operandos forman 8 parejas (a, b). Cada pareja genera 2 casillas: una con a+b y otra con a×b.',
          'En cada casilla escribe los operandos a y b, y pulsa el botón central para cambiar + / ×.',
          'Una casilla con borde teal punteado tiene aritmética correcta. Naranja = incorrecta.',
          'El puzzle tiene solución única garantizada.',
        ]}
        howToPlay="Escribe los dos operandos en cada celda y pulsa el botón + / × para cambiar la operación. Cuando toda la cuadrícula y la tira estén bien, pulsa '✓ Validar'. ¡O espera a que la victoria aparezca automáticamente!"
        author="The Times (UK)"
      />

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{ tiempo: Math.floor((Date.now() - startTime) / 1000), movimientos: moves }}
      />
    </div>
  );
}

// ─── Calculadora ─────────────────────────────────────────────────────────────

function CalcContent({
  display, first, op, waiting, onDigit, onDot, onOp, onEquals, onClear, onBackspace,
}: {
  display: string; first: number | null; op: string | null; waiting: boolean;
  onDigit: (d: string) => void; onDot: () => void; onOp: (o: string) => void;
  onEquals: () => void; onClear: () => void; onBackspace: () => void;
}) {
  const rows = [['7','8','9','÷'],['4','5','6','×'],['1','2','3','−'],['0','.','⌫','+']];
  const isOp = (k: string) => ['+','−','×','÷'].includes(k);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', background: '#161d2e' }}>
      <div style={{ padding: '0.75rem 1rem', background: '#0f172a', borderBottom: '1px solid #2a3250', minHeight: 76, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <div style={{ fontSize: '0.65rem', color: '#64748b', textAlign: 'right', minHeight: 16 }}>
          {first !== null && op ? `${first} ${op}` : ' '}
        </div>
        <div style={{ fontSize: '1.7rem', fontWeight: 700, color: '#f1f5f9', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {display}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
        {rows.map(row => row.map(k => (
          <CalcBtn key={k} label={k} isOp={isOp(k)} active={isOp(k) && op === k && waiting}
            onClick={() => {
              if (k === '.') onDot();
              else if (k === '⌫') onBackspace();
              else if (isOp(k)) onOp(k);
              else onDigit(k);
            }}
          />
        )))}
        <CalcBtn label="C" onClick={onClear} span={2} isDelete />
        <CalcBtn label="=" onClick={onEquals} span={2} isEquals />
      </div>
    </div>
  );
}

function CalcBtn({ label, onClick, isOp = false, isDelete = false, isEquals = false, span = 1, active = false }: {
  label: string; onClick: () => void;
  isOp?: boolean; isDelete?: boolean; isEquals?: boolean; span?: number; active?: boolean;
}) {
  return (
    <button onClick={onClick} style={{
      gridColumn: span > 1 ? `span ${span}` : undefined,
      background: isEquals ? '#7c3aed' : isDelete ? 'rgba(239,68,68,0.12)' : active ? 'rgba(124,58,237,0.35)' : isOp ? 'rgba(124,58,237,0.12)' : '#161d2e',
      color: isEquals ? 'white' : isDelete ? '#ef4444' : isOp ? '#a78bfa' : '#f1f5f9',
      border: '1px solid #2a3250', padding: '0.9rem', fontSize: '1rem', fontWeight: 600,
      cursor: 'pointer', fontFamily: 'inherit', transition: 'background .15s',
      userSelect: 'none' as const,
    }}>
      {label}
    </button>
  );
}

// ─── Botón icono de la barra superior ────────────────────────────────────────

function TopBtn({ children, title, onClick, disabled = false }: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        background: 'none', border: `1px solid #2a3250`, color: '#94a3b8',
        width: 32, height: 32, borderRadius: 8, cursor: disabled ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: disabled ? 0.3 : 1, transition: 'all .2s',
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}
